import { useCallback, useEffect, useMemo, useRef } from 'react'
import { type CanonicalLoadInput, isLiveLoadSearchSocketEvent } from '@numeo/types-core'
import { LIVE_LOAD_SEARCH_EVENT } from 'features/load-hub/liveSearchSocketEvents'
import { closeLiveSearchesQuietly, closeLiveSearchQuietly } from 'features/load-hub/closeLiveSearchQuietly'

interface BoxTruckLiveSearchHandlers {
    /** A posting the server matched against one of this board's own live searches. */
    onLoadCreated: (boardInstanceId: string, load: CanonicalLoadInput, source: string) => void
    /** That posting left the board — covered, superseded, or expired. */
    onLoadCancelled: (boardInstanceId: string, externalLoadId: string, source: string) => void
}

interface BoxTruckLiveSearch {
    /** Called when a search starts, with the board it runs for and the lane instance ids it is about to run.
     *  Drops the subscriptions THAT BOARD no longer covers, so a narrower search cannot keep receiving the
     *  previous one's postings — and leaves every other tab's alone. */
    beginSearch: (boardInstanceId: string, laneInstanceIds: readonly string[]) => void
    /** Register the subscription the server acknowledged for one lane (`live_search_ready`). */
    activate: (boardInstanceId: string, laneInstanceId: string, subscriptionId: string) => void
    /** That board is gone — a closed tab. Releases its lanes and forgets it. */
    endBoard: (boardInstanceId: string) => void
}

/**
 * Owns the Box Truck board's live-search subscriptions — one per search lane — and turns the socket events
 * they produce into board deltas.
 *
 * This is the board's only live path. The server's broadcast of every new Sylectus posting (`LOADS_CREATED`) is
 * not listened to on the web: it carries no coordinates, so a board could not tell which lane a posting belongs to,
 * and it is delivered to the mobile app, which owns that feed. A subscription is matched by the server against the
 * same criteria the search ran on, and carries cancellations. It is gated per user (`isLiveLoadSearchEnabledForUser`
 * is fail-closed), so an operator outside the rollout gets no live rows and sees new postings on the next refresh.
 */
export const useBoxTruckLiveSearch = (handlers: BoxTruckLiveSearchHandlers): BoxTruckLiveSearch => {
    /** lane instance id → the subscription the server acknowledged for it, and the board it belongs to. */
    const subscriptionsByLaneRef = useRef(new Map<string, { subscriptionId: string; boardInstanceId: string }>())
    /** Per board, the lanes its CURRENT search is running. A `live_search_ready` for anything else is a late
     *  acknowledgement from a search that has already been replaced, and is closed rather than kept.
     *
     *  Keyed by board because one tab's search must not disturb another's: a single flat set could only be
     *  replaced wholesale (wiping other tabs' lanes, so their late acks would be closed) or added to (never
     *  forgetting a lane, so a late ack for a REPLACED search was kept and left a subscription live until
     *  TTL, delivering postings the board had stopped asking for). Per board it can be replaced wholesale. */
    const currentLanesByBoardRef = useRef(new Map<string, Set<string>>())
    // Read through a ref so a handler identity that changes every render cannot tear down the listener, and
    // so a socket event is always applied by the board's current setState closure.
    const handlersRef = useRef(handlers)
    handlersRef.current = handlers

    const close = useCallback((subscriptionId: string, laneInstanceId: string) => {
        closeLiveSearchQuietly(subscriptionId, { surface: 'box_truck', laneInstanceId })
    }, [])

    const beginSearch = useCallback((boardInstanceId: string, laneInstanceIds: readonly string[]) => {
        // Replaced, not merged: whatever this board asked for last is no longer what it is showing.
        currentLanesByBoardRef.current.set(boardInstanceId, new Set(laneInstanceIds))
        const released: string[] = []
        for (const [laneInstanceId, entry] of [...subscriptionsByLaneRef.current]) {
            // Only this board's own lanes. Every tab of the loadboard shares this hook, and a tab that is
            // not on screen keeps its subscription on purpose: switching back to it makes its lanes the
            // active board again and its live feed resumes, with no second search to pay for.
            if (entry.boardInstanceId !== boardInstanceId) continue
            if (laneInstanceIds.includes(laneInstanceId)) continue
            subscriptionsByLaneRef.current.delete(laneInstanceId)
            released.push(entry.subscriptionId)
        }
        // One request for the set. A fleet board has a lane per pinned driver, and releasing them one
        // call at a time multiplied every re-search by that count.
        closeLiveSearchesQuietly(released, { surface: 'box_truck', boardInstanceId, reason: 'lanes-dropped' })
    }, [])

    const activate = useCallback(
        (boardInstanceId: string, laneInstanceId: string, subscriptionId: string) => {
            // The board is PASSED, not recovered from the lane id. Reading it back by slicing off `-lane`
            // would put a second copy of a format `laneSearchInstanceId` owns in the routing path, where
            // getting it wrong is silent: deliveries would simply stop matching a board nobody renamed.
            if (!currentLanesByBoardRef.current.get(boardInstanceId)?.has(laneInstanceId)) {
                close(subscriptionId, laneInstanceId)
                return
            }
            // The server replaces a subscription for the same lane atomically, so the previous id is already
            // cancelled server-side; overwriting is all this side has to do.
            subscriptionsByLaneRef.current.set(laneInstanceId, { subscriptionId, boardInstanceId })
        },
        [close]
    )

    const endBoard = useCallback((boardInstanceId: string) => {
        // A closed tab is not coming back, so its lanes must not stay live. Nothing else released them:
        // `beginSearch` only runs when a board searches AGAIN, and unmount only covers leaving the page —
        // so every closed multi-driver tab used to leave N subscriptions running to TTL, each holding its
        // own Redis H3 index members, plus entries in both maps for the rest of the session.
        currentLanesByBoardRef.current.delete(boardInstanceId)
        const released: string[] = []
        for (const [laneInstanceId, entry] of [...subscriptionsByLaneRef.current]) {
            if (entry.boardInstanceId !== boardInstanceId) continue
            subscriptionsByLaneRef.current.delete(laneInstanceId)
            released.push(entry.subscriptionId)
        }
        closeLiveSearchesQuietly(released, { surface: 'box_truck', boardInstanceId, reason: 'tab-closed' })
    }, [])

    useEffect(() => {
        const subscriptions = subscriptionsByLaneRef.current
        const onLiveEvent = (event: Event) => {
            if (!(event instanceof CustomEvent) || !isLiveLoadSearchSocketEvent(event.detail)) return
            const liveEvent = event.detail
            // Every live subscription in this browser tab shares one window event, so a Load Hub tab's
            // postings arrive here too. The subscription id says which lane it was for — and therefore which
            // BOARD — and that is reported rather than filtered on: a delivery for a tab that is not on
            // screen is still a load that tab's search asked for, and dropping it left the tab's board stale
            // for exactly those rows when the operator switched back to it.
            const owner = [...subscriptions.values()].find((entry) => entry.subscriptionId === liveEvent.subscriptionId)
            if (!owner) return
            if (liveEvent.action === 'loadCancelled') {
                handlersRef.current.onLoadCancelled(owner.boardInstanceId, liveEvent.externalLoadId, liveEvent.source)
                return
            }
            handlersRef.current.onLoadCreated(owner.boardInstanceId, liveEvent.load, liveEvent.source)
        }
        window.addEventListener(LIVE_LOAD_SEARCH_EVENT, onLiveEvent)
        return () => {
            window.removeEventListener(LIVE_LOAD_SEARCH_EVENT, onLiveEvent)
            const open = [...subscriptions.values()].map((entry) => entry.subscriptionId)
            subscriptions.clear()
            // One request matters most HERE: the page is unloading, and a browser drops in-flight requests
            // as it goes. Ten closes mostly do not arrive; one usually does.
            closeLiveSearchesQuietly(open, { surface: 'box_truck', reason: 'unmount' })
        }
    }, [])

    // Stable, because the board's search callback depends on it and is itself a dependency of the page's
    // refresh handlers — a fresh object every render would ripple a new `refresh` identity through all of them.
    return useMemo(() => ({ beginSearch, activate, endBoard }), [beginSearch, activate, endBoard])
}
